import { Feature } from "ol";
import { buffer } from "ol/extent";
import { OL3Parser } from "jsts/org/locationtech/jts/io";
import { BufferOp } from "jsts/org/locationtech/jts/operation/buffer";
import { Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon } from "ol/geom";
import { LinearRing } from "ol/geom";
import { getMap } from "./GeoLiteMap";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Fill, Stroke, Style } from "ol/style";
import { EsriJSON } from "ol/format";
import { IEsriFieldInfo } from "./interfaces";
import { FeatureLike } from "ol/Feature";

export interface IEsriGeometry {
    rings: number[][][];
}

const esrijsonFormat = new EsriJSON();

const parser = new OL3Parser();
parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon);

export const getBuffer = (feature: Feature, distance?: number) =>
    buffer(feature.getGeometry().getExtent(), distance || 100);

export const cloneFeatures = (features: Feature[]): Feature[] => features.map((f) => f.clone());

export const getMultiPolygonGeometry = (features: Feature[]): IEsriGeometry => {
    const bufferFeatures = bufferizeFeatures(cloneFeatures(features), 2);
    // if you want to see buffers, use this function
    // visualizeBuffers(bufferFeatures);

    const geometries = bufferFeatures.map((f) => {
        const esriGeometry = esrijsonFormat.writeGeometry(f.getGeometry());
        return JSON.parse(esriGeometry).rings[0];
    });
    const geom = { hasZ: false, hasM: false, rings: [...geometries] };
    return geom;
};

export const bufferizeFeatures = (features: Feature[], distance?: number): Feature[] => {
    for (let i = 0; i < features.length; i += 1) {
        const jstsGeometry = parser.read(features[i].getGeometry());
        const bo = new BufferOp(jstsGeometry);
        const buffered = bo.getResultGeometry(distance || 10);
        const geometry = parser.write(buffered);

        features[i].setGeometry(geometry.simplify(5));
    }

    return features;
};

const createVectorLayer = (source: VectorSource) =>
    new VectorLayer({
        properties: { id: "visualize-buffers-layer" },
        source,
        style: () => {
            return new Style({
                fill: new Fill({ color: [255, 255, 255, 0.4] }),
                stroke: new Stroke({ color: "blue", width: 1 })
            });
        }
    });

const visualizeBuffers = (features: Feature[]) => getMap().addLayer(createVectorLayer(new VectorSource({ features })));

export const replaceUrlParam = (paramName: string, paramValue: string): string => {
    let url = window.location.href;
    const pattern = new RegExp("\\b(" + paramName + "=).*?(&|#|$)");
    if (url.search(pattern) >= 0) {
        return url.replace(pattern, "$1" + paramValue + "$2");
    }
    url = url.replace(/[?#]$/, "");
    return url + (url.indexOf("?") > 0 ? "&" : "?") + paramName + "=" + paramValue;
};

export const decodeFeatureFields = (feature: Feature, fields: IEsriFieldInfo[]): Feature => {
    const attributes = Object.entries(feature.getProperties())
        .map(([fieldName, fieldValue]) => {
            const fieldInfo = fields && fields.find((field) => field.name === fieldName);
            const domainValue =
                fieldInfo &&
                fieldInfo.domain &&
                fieldInfo.domain.codedValues &&
                fieldInfo.domain.codedValues.find((cv) => cv.code === fieldValue);

            return fieldInfo ? [fieldInfo.alias, (domainValue && domainValue.name) || fieldValue] : null;
        })
        .filter((f) => f);

    const attributeObj = Object.fromEntries(attributes);

    const newFeature = new Feature(feature.getGeometry());
    newFeature.setProperties(attributeObj);

    return newFeature;
};

export const debounce = (func, delay: number) => {
    let timeoutId;

    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
};

export const getUrlParam = (param: string): string => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search.slice(1));

    return params.get(param);
};

export const roundToPlaceValue = (number: number): number => {
    // example: 3980000 => 4000000 etc
    const placeValue = Math.floor(number).toString().length - 1;
    const factor = 10 ** placeValue;

    return Math.round(number / factor) * factor;
};

export const deepClone = (obj) => {
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    if (Array.isArray(obj)) {
        const cloneArray = [];
        for (let i = 0; i < obj.length; i++) {
            cloneArray[i] = deepClone(obj[i]);
        }
        return cloneArray;
    }

    const cloneObj = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            cloneObj[key] = deepClone(obj[key]);
        }
    }
    return cloneObj;
};

export const deepCloneWithChanges = (obj, changes = []) => {
    // Example for `changes` parameter:
    // const changes = [
    //     { key: 'age', remove: true }, // Remove the 'age' key
    //     { key: 'name', value: 'Janis' }, // Change the 'name' value to 'Janis'
    //     { key: 'hobbies', remove: true }, // Remove the 'hobbies' key
    //     { key: 'country', value: 'LV' }, // Add a new key 'country' with value 'LV'
    //   ];
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    if (Array.isArray(obj)) {
        const cloneArray = [];
        for (let i = 0; i < obj.length; i++) {
            cloneArray[i] = deepCloneWithChanges(obj[i], changes);
        }
        return cloneArray;
    }

    const cloneObj = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            let clonedValue = deepCloneWithChanges(obj[key], changes);
            for (const change of changes) {
                if (change.key === key) {
                    if (change.remove) {
                        clonedValue = undefined;
                    } else if (typeof change.value !== "undefined") {
                        clonedValue = change.value;
                    }
                }
            }
            if (typeof clonedValue !== "undefined") {
                cloneObj[key] = clonedValue;
            }
        }
    }
    return cloneObj;
};

export const deepCloneWithConditionalChanges = (obj, changes = []) => {
    // Example for `changes` parameter:
    // const changes = [
    //     { key: 'age', value: 25, condition: 30 }, // Change 'age' to 25 if it's currently 30
    //     { key: 'name', value: 'Jane', condition: 'John' }, // Change 'name' to 'Jane' if it's currently 'John'
    //     { key: 'city', value: 'New York', condition: 'Exampleville' }, // Change 'city' to 'New York' if it's currently 'Exampleville'
    //   ];
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    if (Array.isArray(obj)) {
        const cloneArray = [];
        for (let i = 0; i < obj.length; i++) {
            cloneArray[i] = deepCloneWithConditionalChanges(obj[i], changes);
        }
        return cloneArray;
    }

    const cloneObj = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            let clonedValue = deepCloneWithConditionalChanges(obj[key], changes);
            for (const change of changes) {
                if (change.key === key) {
                    if (change.remove) {
                        clonedValue = undefined;
                    } else if (
                        typeof change.value !== "undefined" &&
                        (!change.condition || obj[key] === change.condition)
                    ) {
                        clonedValue = change.value;
                    }
                }
            }
            if (typeof clonedValue !== "undefined") {
                cloneObj[key] = clonedValue;
            }
        }
    }
    return cloneObj;
};

export const replaceFieldsWithValues = (feature: FeatureLike, text?: string, fields?: IEsriFieldInfo[]): string => {
    if (!text) {
        return;
    }
    const replacedWithValues = text.replace(/[^{}]+(?=})/g, (matched: string) => {
        const fieldInfo = fields && fields.find((field) => field.name === matched);
        if (fieldInfo && fieldInfo.domain && fieldInfo.domain.type === "codedValue") {
            const codedValue = fieldInfo.domain.codedValues.find((cv) => cv.code === feature.get(matched));
            return codedValue && codedValue.name;
        }
        return feature.get(matched);
    });

    return replacedWithValues
        .replace(/{undefined}/g, "")
        .replace(/{null}/g, "")
        .replace(/[{}]/g, "");
};

// Step 1: Extract placeholder values between {}
const placeholderPattern = /\{([^}]+)\}/g;
// Step 2: Extract calculation values between $ and $
const calculationPattern = /\$([^$]+)\$/g;

export const replaceFieldsWithCalculations = (
    feature: FeatureLike,
    text?: string,
    fields?: IEsriFieldInfo[]
): string => {
    if (!text) {
        return;
    }

    const placeholderMatches = Array.from(text.matchAll(placeholderPattern), (match) => match[1]);
    if (!placeholderMatches?.length) {
        return text;
    }

    let newText = text;
    placeholderMatches.map((match1) => {
        const calculationMatches = Array.from(match1.matchAll(calculationPattern), (match) => match[1]);

        if (calculationMatches.length) {
            const [field, operation, precision] = calculationMatches[0].split(",");
            if (operation === "Round") {
                newText = newText.replace(
                    "{" + match1 + "}",
                    Number(replaceValue(field, feature, fields)).toFixed(Number(precision))
                );
            }
        } else {
            newText = newText.replace("{" + match1 + "}", replaceValue(match1, feature, fields));
        }
    });

    return newText;
};

const replaceValue = (text: string, feature: FeatureLike, fields?: IEsriFieldInfo[]) => {
    let decoded = feature.get(text);
    const fieldInfo = fields && fields.find((field) => field.name === text);
    if (fieldInfo && fieldInfo.domain && fieldInfo.domain.type === "codedValue") {
        const codedValue = fieldInfo.domain.codedValues.find((cv) => cv.code === feature.get(text));
        decoded = codedValue && codedValue.name;
    }

    return decoded === "undefined" || decoded === "null" || decoded === "{}" ? "" : decoded;
};
